/* GASSISA
=========================================================================================
* Descripción: Dashboard responsivo para dispositivos portátiles.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
* Revisión | Fecha      | Desarrollador                   | Resumen
* ---------------------------------------------------------------------------------------
* 1        | 26/06/2026 | Lic. José Luis Aragón Cervantes | Creación
* 2        | 11/08/2026 | Lic. José Luis Aragón Cervantes | Fondo completo
* 3        | 11/10/2026 | Lic. José Luis Aragón Cervantes | Planeta neuronal y accesos
* 4        | 11/10/2026 | GASSISA                         | Refactor responsivo
* ---------------------------------------------------------------------------------------
*/

import { Drawer, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Route, Routes } from "react-router-dom";

import SizeSoftXMenu from "../../leyout/SizeSoftXMenu";
import SizeSoftXDashboardHomePortatil from "./SizeSoftXDashboardHomePortatil";

import { useConfiguracion } from "../../../context/System/ConfiguracionContext";
import { useSesion } from "../../../context/System/SesionContext";
import { useLayout } from "../../../context/System/LayoutContext";

import { dashboardPaginas } from "../../../settings/menuModel";

import FrmAccesosPortatil from "../catalogos/accesos/FrmAccesosPortatil.jsx";
import FrmArticulosPortatil from "../catalogos/articulos/FrmArticulosPortatil.jsx";

/* Páginas con implementaciones específicas para portátil. */
const paginasPortatil = {
    Accesos: <FrmAccesosPortatil />,
    Entradas: <FrmArticulosPortatil />,
};

const SizeSoftXDashboardPortatil = () => {
    const { menuClientOpen, menuDashboardOpen, handleCloseMenuClient } =
        useLayout();

    const { theme: temaConfiguracion } = useConfiguracion();
    const { definicionMenu } = useSesion();

    /* Breakpoint equivalente al comportamiento anterior de Hidden lgDown:
       el menú persistente se muestra a partir de lg. */
    const temaMUI = useTheme();
    const mostrarMenuFijo = useMediaQuery(temaMUI.breakpoints.up("lg"));

    const drawerMenuWidth = "clamp(250px, 58vw, 330px)";
    const colorMenu =
        temaConfiguracion?.palette?.kyngblue?.main ?? "#123B65";

    /* Genera las rutas desde el menú configurado y conserva las páginas
       especiales para portátil. */
    const rutas = Object.entries(definicionMenu ?? {})
        .filter(
            ([claveModulo, modulo]) =>
                claveModulo !== "_paths" && modulo?.mostrar
        )
        .flatMap(([claveModulo, modulo]) =>
            Object.entries(modulo?._paginas ?? {}).map(
                ([clavePagina, pagina]) => ({
                    clave: `${claveModulo}-${clavePagina}`,
                    path: pagina.path,
                    elemento:
                        paginasPortatil[clavePagina] ??
                        dashboardPaginas[clavePagina],
                })
            )
        )
        .filter((ruta) => ruta.path && ruta.elemento);

    const estiloMenu = {
        backgroundColor: colorMenu,
        "--dashboard-menu-width": drawerMenuWidth,
    };

    return (
        <div className="portatil-shell">
            {mostrarMenuFijo && menuDashboardOpen && (
                <aside
                    className="menu-area"
                    style={estiloMenu}
                >
                    <SizeSoftXMenu />
                </aside>
            )}

            <Drawer
              open={Boolean(menuClientOpen)}
              anchor="left"
              onClose={handleCloseMenuClient}
              slotProps={{
                  paper: {
                      sx: {
                          backgroundColor: colorMenu,
                          width: drawerMenuWidth,
                          boxSizing: "border-box",
                      },
                  },
              }}
          >
                <div
                    className="portatil-menu"
                    style={estiloMenu}
                >
                    <SizeSoftXMenu />
                </div>
            </Drawer>

            <main className="portatil-main">
                <Routes>
                    {rutas.map((ruta) => (
                        <Route
                            key={ruta.clave}
                            path={ruta.path}
                            element={ruta.elemento}
                        />
                    ))}

                    <Route
                        path="/*"
                        element={<SizeSoftXDashboardHomePortatil />}
                    />
                </Routes>
            </main>
        </div>
    );
};

export default SizeSoftXDashboardPortatil;